import type Player from 'video.js/dist/types/player';
import { useCallback, useRef, useState } from 'react';
import {
  faArrowsRotate,
  faTowerBroadcast,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, CopyableText, Labeled, Stack } from '@sixui/ui';
import { Trans, useTranslation } from 'react-i18next';
import MediaError from 'video.js/dist/types/media-error';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetLiveStreamsItemResponseBody } from '~/lib/api/modules/LiveStreams';
import { List } from '~/components/atoms/List';
import { Quote } from '~/components/atoms/Quote';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { HlsVideoPlayer } from '~/components/molecules/HlsVideoPlayer';
import { useAppRegion } from '~/hooks/useAppRegion';
import { appRegionsSettings } from '~/lib/appSettings';

const RECORDING_WIDTH = 320;
const RECORDING_HEIGHT = Math.round(RECORDING_WIDTH / 1.8);

export type ILiveStreamBroadcastingProps = IContainerProps & {
  liveStream: IApiGetLiveStreamsItemResponseBody;
};

export const LiveStreamBroadcasting: React.FC<ILiveStreamBroadcastingProps> = (
  props,
) => {
  const { liveStream } = props;
  const { t } = useTranslation();
  const playerRef = useRef<Player | null>(null);
  const [playerError, setPlayerError] = useState<MediaError | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const appRegion = useAppRegion();

  const handlePlayerReady = useCallback((player: Player): void => {
    playerRef.current = player;

    player.on('error', () => {
      setLoading(false);

      const error = player.error();
      if (!error) {
        return;
      }

      setPlayerError((playerError) =>
        error.code === playerError?.code ? playerError : error,
      );
    });

    player.on('loadstart', () => {
      setLoading(false);
      player.play()?.catch(() => {});
    });

    player.on('playing', () => {
      setPlayerError(undefined);
    });
  }, []);

  const handleReloadPlaybackStream = useCallback((): void => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    setLoading(true);
    player.load();
  }, []);

  const iframeCode = `<iframe src="${appRegionsSettings[appRegion].apiUrl}/liveStream/${liveStream.appId},${liveStream._id}/view" frameborder="0" allowfullscreen="true" style="overflow:hidden;height:56vw;width:100%"></iframe>`;

  return (
    <ContentBlock>
      <ContentBlock.Title
        supportingText={t('LiveStreamBroadcasting.supportingText')}
        icon={<FontAwesomeIcon icon={faTowerBroadcast} />}
      >
        {t('LiveStreamBroadcasting.title')}
      </ContentBlock.Title>

      <ContentBlock.InfoList>
        <ContentBlock.InfoListItem
          label={t('LiveStreamBroadcasting.fields.playbackUrl.label')}
        >
          <CopyableText>{liveStream.playbackUrl}</CopyableText>
        </ContentBlock.InfoListItem>

        <ContentBlock.InfoListItem
          label={t('LiveStreamBroadcasting.fields.iframe.label')}
        >
          <CopyableText text={iframeCode}>
            {t('LiveStreamBroadcasting.fields.iframe.value')}
          </CopyableText>
        </ContentBlock.InfoListItem>
      </ContentBlock.InfoList>

      {liveStream.playbackUrl && (
        <ContentBlock.Body>
          <Stack horizontal>
            <Labeled label={t('LiveStreamBroadcasting.preview')}>
              <HlsVideoPlayer
                src={liveStream.playbackUrl}
                width={RECORDING_WIDTH}
                height={RECORDING_HEIGHT}
                preload="auto"
                controls
                playButton
                onReady={handlePlayerReady}
              />
            </Labeled>
          </Stack>

          {playerError && (
            <Stack gap={2}>
              <p>{t('LiveStreamBroadcasting.errors.general')}</p>
              <Quote hasError>{playerError.message}</Quote>
              {playerError.code === 4 && (
                <>
                  <Trans
                    i18nKey="LiveStreamBroadcasting.errors.invalidStream.p1"
                    as="p"
                  />
                  <List>
                    <List.Item>
                      <Trans i18nKey="LiveStreamBroadcasting.errors.invalidStream.listItem1" />
                    </List.Item>
                    <List.Item>
                      <Trans i18nKey="LiveStreamBroadcasting.errors.invalidStream.listItem2" />
                    </List.Item>
                    <List.Item>
                      <Trans
                        i18nKey="LiveStreamBroadcasting.errors.invalidStream.listItem3"
                        as={List.Item}
                      />
                    </List.Item>
                  </List>
                </>
              )}
              <Trans
                i18nKey="LiveStreamBroadcasting.errors.invalidStream.p2"
                as="p"
              />
              <Stack horizontal gap={2}>
                <Button
                  loading={loading}
                  loadingAnimation="halfSpin"
                  icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                  onClick={handleReloadPlaybackStream}
                  variant="outlined"
                >
                  {t('LiveStreamBroadcasting.reloadStream')}
                </Button>
              </Stack>
            </Stack>
          )}
        </ContentBlock.Body>
      )}
    </ContentBlock>
  );
};
